import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getNotificationSummary } from 'api/me';
import { useAuth } from 'contexts/AuthContext';
import { ME_QUERY_KEY } from './useMe';
import type { TNotificationSummary } from 'types';

/** Account スコープなので組織切り替えでも破棄されない（`/me` 配下の key） */
export const ME_NOTIFICATION_SUMMARY_QUERY_KEY = [
  ...ME_QUERY_KEY,
  'notification-summary',
] as const;

/** 設計書 §9.8: 個人チャネルのイベント以外にも5分ごとに再取得する */
export const NOTIFICATION_SUMMARY_REFETCH_INTERVAL_MS = 5 * 60 * 1000;

/** 設計書 §9.8: 選択していない組織のイベントは1秒デバウンスで再取得する */
export const NOTIFICATION_SUMMARY_DEBOUNCE_MS = 1_000;

export function useNotificationSummary() {
  const { user: firebaseUser, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ME_NOTIFICATION_SUMMARY_QUERY_KEY,
    queryFn: getNotificationSummary,
    enabled: !authLoading && !!firebaseUser,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: NOTIFICATION_SUMMARY_REFETCH_INTERVAL_MS,
    retry: 0,
  });
}

/** organizationId → 未読数（チャット非対応プランは null のまま） */
export function useNotificationSummaryByOrganization(): Map<
  number,
  number | null
> {
  const { data } = useNotificationSummary();
  return toUnreadCountByOrganization(data);
}

export function toUnreadCountByOrganization(
  summary: TNotificationSummary | undefined,
): Map<number, number | null> {
  const map = new Map<number, number | null>();
  for (const organization of summary?.organizations ?? []) {
    map.set(organization.organizationId, organization.maleUnreadCount);
  }
  return map;
}

/** サマリー再取得を 1 秒デバウンスでまとめる。個人チャネルのイベント・再接続・復帰で使う。 */
export function useDebouncedRefetchNotificationSummary() {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledRefetch = useCallback(() => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const scheduleRefetch = useCallback(() => {
    // 待機中の再取得があれば追加のイベントはまとめる
    if (timerRef.current !== null) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void queryClient.refetchQueries({
        queryKey: ME_NOTIFICATION_SUMMARY_QUERY_KEY,
      });
    }, NOTIFICATION_SUMMARY_DEBOUNCE_MS);
  }, [queryClient]);

  useEffect(() => cancelScheduledRefetch, [cancelScheduledRefetch]);

  return { scheduleRefetch, cancelScheduledRefetch };
}
